import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileText, Sparkles, ShieldCheck, Info, Download } from "lucide-react";
import AppShell from "@/components/app/AppShell";
import { DocStatusBadge } from "@/components/StatusBadge";
import api, { apiErrorMessage, API_BASE, tokenParam } from "@/lib/api";
import { toast } from "sonner";

const FIELD_DEFS = [
  ["full_name", "Name on document"],
  ["date_of_birth", "Date of birth"],
  ["passport_number", "Passport number"],
  ["document_date", "Document date"],
  ["period_start", "Statement period start"],
  ["period_end", "Statement period end"],
  ["closing_balance", "Closing balance"],
  ["institution_name", "Institution / employer"],
];

const inputCls = "w-full rounded-lg border border-hborder bg-white px-3 py-2 text-sm text-navy focus:border-tblue";

export default function DocumentDetail() {
  const { caseId, docId } = useParams();
  const [doc, setDoc] = useState(null);
  const [fields, setFields] = useState({});
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const load = useCallback(() => {
    api.get(`/documents/${docId}`).then((r) => { setDoc(r.data); setFields(r.data.declared_fields || {}); }).catch(() => setDoc(false));
  }, [docId]);
  useEffect(() => { load(); }, [load]);

  if (doc === false) return <AppShell title="Document"><p className="text-muted">This document could not be loaded.</p></AppShell>;
  if (!doc) return <AppShell><div className="h-40 animate-pulse rounded-md bg-white" /></AppShell>;

  const saveFields = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(fields).filter(([, v]) => v));
      await api.put(`/documents/${docId}/declared-fields`, { fields: clean });
      toast.success("Details saved"); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runExtract = async () => {
    if (!text.trim()) { toast.error("Paste the document text first"); return; }
    setExtracting(true);
    try { await api.post(`/documents/${docId}/extract`, { text }); toast.success("Extraction complete"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setExtracting(false); }
  };

  const version = (doc.versions || []).find((v) => v.id === doc.current_version);
  const isImage = version?.mime_type?.startsWith("image/");
  const fileUrl = `${API_BASE}/documents/${docId}/file${tokenParam()}`;

  return (
    <AppShell title="Document" back={{ to: `/app/cases/${caseId}`, label: "Back to case" }}>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-md border border-hborder bg-white p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-lg bg-softcream"><FileText size={20} className="text-navy" /></span>
                <div><p className="font-semibold text-heading">{doc.original_filename}</p><p className="text-xs text-muted">Version {(doc.versions || []).length} · original evidence is immutable</p></div>
              </div>
              <DocStatusBadge status={doc.status} />
            </div>
            <a href={fileUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-tblue hover:underline" data-testid="doc-download-link">
              <Download size={14} /> View file
            </a>
            {isImage && <img src={fileUrl} alt="Uploaded document" className="mt-4 max-h-80 rounded-lg border border-hborder object-contain" />}
          </div>

          <div className="rounded-md border border-hborder bg-white p-5">
            <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-tblue" /><h3 className="font-semibold text-heading">Declared details</h3></div>
            <p className="mt-1 text-sm text-muted">You provide these (provenance: user-declared). They power the consistency checks.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {FIELD_DEFS.map(([key, label]) => (
                <div key={key}>
                  <label className="mb-1 block text-xs font-medium text-navy" htmlFor={key}>{label}</label>
                  <input id={key} className={inputCls} value={fields[key] || ""} onChange={(e) => setFields({ ...fields, [key]: e.target.value })} data-testid={`field-${key}`} />
                </div>
              ))}
            </div>
            <button onClick={saveFields} disabled={busy} className="mt-4 rounded bg-navy px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="save-fields-btn">
              {busy ? "Saving…" : "Save details"}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-md border border-hborder bg-white p-5">
            <div className="flex items-center gap-2"><Sparkles size={18} className="text-coral" /><h3 className="font-semibold text-heading">Assisted extraction</h3></div>
            <p className="mt-1 text-sm text-muted">Paste the text of this document and we will suggest fields. Suggestions never overwrite your declared details.</p>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder="Paste document text here…" className="mt-3 w-full rounded-lg border border-hborder bg-white px-3 py-2 text-sm text-navy focus:border-tblue" data-testid="extract-text" />
            <button onClick={runExtract} disabled={extracting} className="mt-3 w-full rounded border border-coral bg-orange-50 px-4 py-2.5 text-sm font-semibold text-coral disabled:opacity-60" data-testid="run-extract-btn">
              {extracting ? "Analyzing…" : "Suggest fields with AI"}
            </button>
            {doc.extraction && (
              <div className="mt-4 rounded-lg border border-hborder bg-softcream p-3" data-testid="extraction-result">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted">AI suggested ({doc.extraction.model})</p>
                {doc.extraction.summary && <p className="mt-1 text-sm text-navy">{doc.extraction.summary}</p>}
                {doc.extraction.status === "Failed" ? (
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-coral"><Info size={13} /> Automatic extraction unavailable. Enter details manually.</p>
                ) : (
                  <div className="mt-2 space-y-1.5">
                    {(doc.extraction.fields || []).map((f) => (
                      <div key={f.name} className="flex items-center justify-between text-sm">
                        <span className="text-muted">{f.name}</span>
                        <button onClick={() => setFields({ ...fields, [f.name]: f.value })} className="rounded px-2 py-0.5 text-navy hover:bg-white" data-testid={`accept-${f.name}`}>
                          {f.value} <span className="text-xs text-tblue">· use</span>
                        </button>
                      </div>
                    ))}
                    {(doc.extraction.fields || []).length === 0 && <p className="text-xs text-muted">No fields detected.</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
